import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QUEUE_ENTRY_POINTS } from '../../lib/agentPrompts';
import { supabase } from '../../lib/supabase';
import { TASK_TYPES, taskTypeOf } from '../../lib/taskTypes';
import OperatorDesignGallery from '../ai/operator/OperatorDesignGallery';
import { Badge, Card } from '../ui';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };

function Stages({ stages }: { stages: string[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      {stages.map((s, i) => (
        <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Badge tone={i === 0 ? 'accent' : 'neutral'}>{s}</Badge>
          {i < stages.length - 1 ? <span style={faint}>→</span> : null}
        </span>
      ))}
    </div>
  );
}

/** Job counts per task type, for the list. */
function useTypeCounts() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      TASK_TYPES.map(async (t) => {
        const { count } = await supabase.from('ai_jobs').select('id', { count: 'exact', head: true }).eq('task_type', t.id);
        return [t.id, count ?? 0] as const;
      }),
    ).then((rows) => {
      if (!cancelled) setCounts(Object.fromEntries(rows));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return counts;
}

/**
 * Agents → Task types: the library of job shapes (src/lib/taskTypes.ts). The list says what each type's spec holds
 * and which buttons create it; a type opens to its design preview, a sample job at every stage
 * (/agents/task-types/:typeId?stage=…).
 */
export default function TaskTypesView() {
  const { typeId } = useParams();
  const counts = useTypeCounts();
  const type = typeId ? taskTypeOf(typeId) : null;
  const buttons = useMemo(() => (type ? QUEUE_ENTRY_POINTS.filter((e) => e.taskType === type.id && !e.buildStep) : []), [type]);

  if (type) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div>
          <Link to="/agents/task-types" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', textDecoration: 'none' }}>
            ← All task types
          </Link>
        </div>
        <Card eyebrow="Task type" title={type.label} actions={<Badge tone="neutral">{counts[type.id] ?? '…'} jobs</Badge>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
            <Stages stages={type.stages} />
            <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--text-body)', lineHeight: 'var(--leading-snug)' }}>
              {type.detail.map((d) => (
                <li key={d} style={{ marginBottom: '4px' }}>
                  {d}
                </li>
              ))}
            </ul>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
                Buttons that create it
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {buttons.map((b) => (
                  <Link key={b.id} to={`/agents/prompts/${b.id}`} style={{ textDecoration: 'none', maxWidth: '100%' }} title={`${b.where} · see its prompt`}>
                    {/* Long button names wrap inside the pill instead of running off a phone screen. */}
                    <Badge tone="info" style={{ maxWidth: '100%', height: 'auto', minHeight: '20px', padding: '4px 8px', whiteSpace: 'normal' }}>
                      {b.label}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Card>
        <OperatorDesignGallery key={type.id} typeId={type.id} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 'var(--space-4)', alignItems: 'start' }}>
      {TASK_TYPES.map((t) => {
        const made = QUEUE_ENTRY_POINTS.filter((e) => e.taskType === t.id && !e.buildStep);
        return (
          <Link key={t.id} to={`/agents/task-types/${t.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Card interactive eyebrow="Task type" title={t.label} actions={<Badge tone="neutral">{counts[t.id] ?? '…'} jobs</Badge>}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
                <span style={{ color: 'var(--text-muted)' }}>{t.blurb}</span>
                <Stages stages={t.stages} />
                <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                  Created by: {made.map((m) => m.label).join(' · ')}
                </span>
                <span style={{ color: 'var(--accent-text)', fontSize: 'var(--text-xs)' }}>Open the design preview →</span>
              </div>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
